import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, functions as firebaseFunctions } from "../firebase";
import { mapFirebaseError } from "../utils/userErrors";
import {
  AI_GUIDE_SESSION_EVENT_CLEAR,
  AI_GUIDE_SESSION_EVENT_REQUEST_HIGHLIGHT,
  AI_GUIDE_SESSION_EVENT_START,
  AI_GUIDE_SESSION_EVENT_STATUS,
  AI_GUIDE_VIEW_LABELS,
  getGuideTargetDetails
} from "../utils/aiGuide";

const AI_INTRO_MESSAGE =
  "Hi! I’m FamilySync AI Help. Ask me how to set up features, workflows, or troubleshoot issues.";
const AI_LOCAL_GENERIC_REPLY_PREFIX = "I can give step-by-step FamilySync help.";

const AI_GUIDE_TARGET_EXPANSIONS = {
  "settings-add-kid": ["settings-add-kid-input", "settings-add-kid-button"]
};
const AI_GUIDE_TARGET_PREREQUISITES = {
  "settings-kids-tab": ["settings-section-settings"],
  "settings-add-kid-input": ["settings-section-settings", "settings-kids-tab"],
  "settings-add-kid-button": ["settings-section-settings", "settings-kids-tab", "settings-add-kid-input"],
  "settings-home-setup": ["settings-section-settings"],
  "settings-add-family-input": ["settings-section-family"],
  "settings-add-family-button": ["settings-section-family", "settings-add-family-input"],
  "calendar-event-title-input": ["parent-calendar-card"],
  "calendar-event-date-input": ["parent-calendar-card", "calendar-event-title-input"],
  "calendar-event-add-button": ["parent-calendar-card", "calendar-event-title-input", "calendar-event-date-input"]
};

const LOCAL_AI_GUIDE_RULES = [
  {
    keywords: ["add kid", "add child", "new kid", "kids tab", "adding kid", "adding a kid"],
    guide: {
      view: "settings",
      settingsSection: "settings",
      settingsTab: "kids",
      targets: [
        "nav-settings",
        "settings-section-settings",
        "settings-kids-tab",
        "settings-add-kid-input",
        "settings-add-kid-button"
      ]
    }
  },
  {
    keywords: ["add family", "add a family", "create family", "create a family", "new family"],
    guide: {
      view: "settings",
      settingsSection: "family",
      targets: [
        "nav-settings",
        "settings-section-family",
        "settings-add-family-input",
        "settings-add-family-button"
      ]
    }
  },
  {
    keywords: [
      "add calendar event",
      "add a calendar event",
      "create calendar event",
      "create a calendar event",
      "calendar event",
      "add event to calendar"
    ],
    guide: {
      view: "parent",
      targets: [
        "nav-parent",
        "parent-calendar-card",
        "calendar-event-title-input",
        "calendar-event-date-input",
        "calendar-event-add-button"
      ]
    }
  },
  {
    keywords: ["calendar sync", "sync calendar", "google calendar", "outlook calendar", "connect calendar"],
    guide: {
      view: "settings",
      settingsSection: "integrations",
      targets: ["nav-settings", "settings-section-integrations"]
    }
  },
  {
    keywords: ["add chore", "create chore", "add task", "create task"],
    guide: {
      view: "parent",
      targets: ["nav-parent", "parent-task-card", "task-title-input"]
    }
  },
  {
    keywords: ["approval", "approve", "pending"],
    guide: {
      view: "parent",
      targets: ["nav-parent", "parent-approvals-card"]
    }
  },
  {
    keywords: ["clarification", "clarify", "question on task"],
    guide: {
      view: "parent",
      targets: ["nav-parent", "parent-clarifications-card"]
    }
  },
  {
    keywords: ["layout", "resize", "drag card", "cards"],
    guide: {
      view: "parent",
      targets: ["layout-toolbar", "card-controls"]
    }
  },
  {
    keywords: ["feedback", "help center", "reply"],
    guide: {
      view: "feedback",
      targets: ["nav-feedback"]
    }
  },
  {
    keywords: ["home setup", "shared device", "tablet setup"],
    guide: {
      view: "settings",
      settingsSection: "settings",
      settingsTab: "homeSetup",
      targets: ["nav-settings", "settings-section-settings", "settings-home-setup"]
    }
  }
];

const normalizeGuideTargets = (targets) => {
  if (!Array.isArray(targets)) return [];
  const normalized = [];
  const seen = new Set();
  const addTarget = (rawValue) => {
    const next = String(rawValue || "").trim();
    if (!next || seen.has(next)) return;
    seen.add(next);
    normalized.push(next);
  };
  targets.forEach((rawTarget) => {
    const targetId = String(rawTarget || "").trim();
    if (!targetId) return;
    const expanded = AI_GUIDE_TARGET_EXPANSIONS[targetId] || [targetId];
    expanded.forEach((value) => {
      const next = String(value || "").trim();
      if (!next) return;
      const prerequisites = AI_GUIDE_TARGET_PREREQUISITES[next] || [];
      prerequisites.forEach(addTarget);
      addTarget(next);
    });
  });
  return normalized;
};

const deriveLocalGuideFromMessage = (rawMessage) => {
  const normalizedMessage = normalizeAiIntentText(rawMessage);
  if (!normalizedMessage) return null;
  if (aiMessageIsAddCalendarEventIntent(normalizedMessage)) {
    return {
      view: "parent",
      targets: normalizeGuideTargets([
        "nav-parent",
        "parent-calendar-card",
        "calendar-event-title-input",
        "calendar-event-date-input",
        "calendar-event-add-button"
      ])
    };
  }
  if (aiMessageIsAddFamilyIntent(normalizedMessage)) {
    return {
      view: "settings",
      settingsSection: "family",
      targets: normalizeGuideTargets([
        "nav-settings",
        "settings-section-family",
        "settings-add-family-input",
        "settings-add-family-button"
      ])
    };
  }
  if (aiMessageIsCalendarSyncIntent(normalizedMessage)) {
    return {
      view: "settings",
      settingsSection: "integrations",
      targets: normalizeGuideTargets(["nav-settings", "settings-section-integrations"])
    };
  }
  let best = null;
  for (const rule of LOCAL_AI_GUIDE_RULES) {
    const score = rule.keywords.reduce(
      (count, keyword) => count + (aiMessageMatchesKeyword(normalizedMessage, keyword) ? 1 : 0),
      0
    );
    if (score <= 0) continue;
    if (!best || score > best.score) best = { score, guide: rule.guide };
  }
  if (!best?.guide) return null;
  return {
    ...best.guide,
    targets: normalizeGuideTargets(best.guide.targets)
  };
};

const buildAiGuidePlan = (guide) => {
  if (!guide) return null;
  const steps = [];
  const seen = new Set();
  const pushStep = (step) => {
    if (!step) return;
    const title = String(step.title || "").trim();
    const instruction = String(step.instruction || "").trim();
    if (!title || !instruction) return;
    const key = `${title}::${instruction}`;
    if (seen.has(key)) return;
    seen.add(key);
    steps.push({
      title,
      instruction,
      context: String(step.context || "").trim()
    });
  };
  const targets = normalizeGuideTargets(guide.targets);
  if (guide.view && AI_GUIDE_VIEW_LABELS[guide.view]) {
    pushStep({
      title: `Open ${AI_GUIDE_VIEW_LABELS[guide.view]}`,
      instruction: `Use the top navigation to open ${AI_GUIDE_VIEW_LABELS[guide.view]}.`,
      context: "This puts you on the right page for the next action."
    });
  }
  if (guide.settingsTab === "kids") {
    pushStep({
      title: "Open Kids Tab",
      instruction: "In Settings, switch to the Kids tab.",
      context: "Kid accounts and PIN setup are managed there."
    });
  }
  targets.slice(0, 6).forEach((targetId) => {
    const details = getGuideTargetDetails(targetId);
    pushStep({
      title: details.title,
      instruction: details.instruction,
      context: details.context
    });
  });
  if (steps.length === 0) return null;
  return {
    title: "Guide Steps",
    steps
  };
};

const isHelpThread = (thread) =>
  String(thread?.category || "").toLowerCase() === "admin-help" ||
  String(thread?.view || "").toLowerCase() === "help";

const isThreadClearedForUser = (thread, uid) =>
  Array.isArray(thread?.clearedBy) &&
  thread.clearedBy.some((value) => String(value || "") === String(uid || ""));

const responseAuthorLabel = (response, currentUser) => {
  const role = String(response?.authorRole || "").toLowerCase();
  const responseEmail = String(response?.authorEmail || "").toLowerCase();
  const currentEmail = String(currentUser?.email || "").toLowerCase();
  if (role === "admin") return "Admin";
  if (role === "family" || role === "user") {
    if (responseEmail && currentEmail && responseEmail === currentEmail) return "You";
    if (response?.authorName) return response.authorName;
    return "Family";
  }
  if (response?.adminName) return response.adminName;
  return response?.authorName || "Reply";
};

const isResponseFromCurrentUser = (response, currentUser) => {
  const role = String(response?.authorRole || "").toLowerCase();
  const responseEmail = String(response?.authorEmail || "").toLowerCase();
  const currentEmail = String(currentUser?.email || "").toLowerCase();
  return role === "family" || role === "user" || (responseEmail && currentEmail && responseEmail === currentEmail);
};

const normalizeAiIntentText = (raw) =>
  String(raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const AI_WORD_VARIANTS = {
  adding: "add",
  added: "add",
  creates: "create",
  creating: "create",
  kids: "kid",
  children: "child",
  chores: "chore",
  tasks: "task"
};

const normalizeAiWord = (raw) => {
  const base = String(raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .trim();
  if (!base) return "";
  let word = AI_WORD_VARIANTS[base] || base;
  if (word.endsWith("ies") && word.length > 4) {
    word = `${word.slice(0, -3)}y`;
  } else if (word.endsWith("ing") && word.length > 5) {
    word = word.slice(0, -3);
  } else if (word.endsWith("ed") && word.length > 4) {
    word = word.slice(0, -2);
  } else if (word.endsWith("s") && word.length > 3) {
    word = word.slice(0, -1);
  }
  return AI_WORD_VARIANTS[word] || word;
};

const normalizeAiTokens = (raw) =>
  normalizeAiIntentText(raw)
    .split(" ")
    .map((token) => normalizeAiWord(token))
    .filter(Boolean);

const aiMessageMatchesKeyword = (normalizedMessage, rawKeyword) => {
  const message = normalizeAiIntentText(normalizedMessage);
  const keyword = normalizeAiIntentText(rawKeyword);
  if (!message || !keyword) return false;
  const messageWords = normalizeAiTokens(message);
  const keywordWords = normalizeAiTokens(keyword);
  if (keywordWords.length === 0) return false;
  if (keywordWords.length === 1) return messageWords.includes(keywordWords[0]);
  if (message.includes(keyword)) return true;
  const uniqueWords = new Set(messageWords);
  return keywordWords.every((word) => uniqueWords.has(word));
};

const aiMessageTokenSet = (rawMessage) => new Set(normalizeAiTokens(rawMessage));

const aiMessageHasAnyToken = (tokens, words) =>
  Array.isArray(words) && words.some((word) => tokens.has(normalizeAiWord(word)));

const aiMessageHasActionToken = (tokens) =>
  aiMessageHasAnyToken(tokens, ["add", "create", "new", "make", "start"]);

const aiMessageIsAddFamilyIntent = (rawMessage) => {
  const tokens = aiMessageTokenSet(rawMessage);
  return tokens.has("family") && aiMessageHasActionToken(tokens);
};

const aiMessageIsAddCalendarEventIntent = (rawMessage) => {
  const tokens = aiMessageTokenSet(rawMessage);
  const hasEventLike = tokens.has("event") || tokens.has("calendar");
  const hasCalendarLike = tokens.has("calendar") || tokens.has("schedule");
  return hasEventLike && hasCalendarLike && aiMessageHasActionToken(tokens);
};

const aiMessageIsCalendarSyncIntent = (rawMessage) => {
  const tokens = aiMessageTokenSet(rawMessage);
  if (!tokens.has("calendar")) return false;
  return aiMessageHasAnyToken(tokens, [
    "sync",
    "connect",
    "link",
    "import",
    "export",
    "google",
    "outlook",
    "microsoft"
  ]);
};

const buildLocalAiFallbackReply = (rawMessage) => {
  const message = normalizeAiIntentText(rawMessage);
  const has = (keywords) =>
    Array.isArray(keywords) &&
    keywords.some((keyword) => aiMessageMatchesKeyword(message, keyword));
  const isAddFamilyIntent =
    aiMessageIsAddFamilyIntent(message) ||
    has(["add family", "add a family", "create family", "create a family", "new family"]);
  const isAddCalendarEventIntent =
    aiMessageIsAddCalendarEventIntent(message) ||
    has([
      "add calendar event",
      "add a calendar event",
      "create calendar event",
      "create a calendar event",
      "calendar event",
      "add event to calendar"
    ]);
  const isCalendarSyncIntent =
    !isAddCalendarEventIntent &&
    (aiMessageIsCalendarSyncIntent(message) ||
      has(["calendar sync", "sync calendar", "google calendar", "outlook calendar", "connect calendar"]));

  if (has(["add kid", "add a kid", "add child", "new kid"])) {
    return [
      "Quick answer: use Settings -> App Settings -> Kids -> Add Child.",
      "1. In the top navigation, open Settings.",
      "2. In Settings, open App Settings section.",
      "3. In App Settings, select the Kids tab.",
      "4. In Add Child, type the child name.",
      "5. Press Add Child.",
      "6. Confirm the new child appears in your kids list.",
      "If Add Child is missing, confirm you are on a parent account."
    ].join("\n");
  }
  if (isAddFamilyIntent) {
    return [
      "Quick answer: use Settings -> Family & Roles -> Add Family.",
      "1. In the top navigation, open Settings.",
      "2. In Settings, open Family & Roles section.",
      "3. In New family name, type the family name.",
      "4. Press Add Family.",
      "5. Confirm the new family appears in the family list."
    ].join("\n");
  }
  if (isAddCalendarEventIntent) {
    return [
      "Quick answer: add calendar events from Parent Hub -> Calendar.",
      "1. In the top navigation, open Parent Hub.",
      "2. In Parent Hub, find the Calendar card.",
      "3. Enter Event Title.",
      "4. Pick Event Date (required).",
      "5. Optional: set Event Time.",
      "6. Press Add Event."
    ].join("\n");
  }
  if (has(["add task", "add chore", "create chore", "create task"])) {
    return [
      "Quick answer: add chores from Parent Hub -> Assign Chores.",
      "1. Open Parent Hub.",
      "2. In Assign Chores, enter title, points, and assignee.",
      "3. Save the chore.",
      "4. For repeats, set Frequency to Repeat before saving."
    ].join("\n");
  }
  if (has(["approval", "approve", "pending"])) {
    return [
      "To approve chores:",
      "1. Open Parent Hub.",
      "2. Open Approvals.",
      "3. Approve or request clarification."
    ].join("\n");
  }
  if (has(["clarification", "clarify"])) {
    return [
      "Clarifications are handled separately:",
      "1. Open Parent Hub.",
      "2. Open Clarifications.",
      "3. Reply and resolve."
    ].join("\n");
  }
  if (has(["join family", "invite code", "rejoin", "login loop"])) {
    return [
      "If a joined member is sent back to Join/Create:",
      "1. Confirm they are signing in with the same login used to join.",
      "2. Sign out and sign in again to refresh family membership.",
      "3. If it keeps happening, send Admin Help with the account email and family name."
    ].join("\n");
  }
  if (has(["pro", "pricing", "upgrade", "plan"])) {
    return [
      "Plan summary:",
      "1. Free has core limits.",
      "2. Pro unlocks expanded family workflows and sharing.",
      "3. For billing help, use Admin Help Panel."
    ].join("\n");
  }
  if (isCalendarSyncIntent) {
    return [
      "Quick answer: connect calendar sync from Settings -> Calendar Sync.",
      "1. Open Settings.",
      "2. Open Calendar Sync section.",
      "3. Connect Google or Microsoft with OAuth.",
      "4. Choose sync mode (import/export/both)."
    ].join("\n");
  }
  return [
    "I can give step-by-step FamilySync help.",
    "Tell me: platform (web/iOS/android/macOS), screen name, what you clicked, and what happened.",
    "If you need a human, use Admin Help Panel."
  ].join("\n");
};

const isGenericLocalFallbackReply = (replyText) =>
  String(replyText || "").trim().startsWith(AI_LOCAL_GENERIC_REPLY_PREFIX);

const isWeakAiReply = (rawReply) => {
  const reply = String(rawReply || "").trim().toLowerCase();
  if (!reply) return true;
  const hasListSteps = /\b1[.)]\s/.test(reply) || /\n2[.)]\s/.test(reply);
  if (hasListSteps && !reply.includes("tell me: platform")) return false;
  const weakPhrases = [
    "i can help with setup",
    "i can give step-by-step familysync help",
    "tell me the exact action",
    "tell me: platform",
    "please provide more details",
    "please share more details",
    "can you clarify"
  ];
  return weakPhrases.some((phrase) => reply.includes(phrase));
};

export default function FeedbackDashboard({ familyId }) {
  const auth = getAuth();
  const user = auth.currentUser;
  const askHelpAssistant = useMemo(
    () => httpsCallable(firebaseFunctions, "askHelpAssistant"),
    []
  );
  const submitFeedback = useMemo(
    () => httpsCallable(firebaseFunctions, "submitFeedback"),
    []
  );
  const submitAdminHelpRequest = useMemo(
    () => httpsCallable(firebaseFunctions, "submitAdminHelpRequest"),
    []
  );
  const appendSupportThreadMessage = useMemo(
    () => httpsCallable(firebaseFunctions, "appendSupportThreadMessage"),
    []
  );
  const clearSupportThreadForUser = useMemo(
    () => httpsCallable(firebaseFunctions, "clearSupportThreadForUser"),
    []
  );
  const [threads, setThreads] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [adminHelpMessage, setAdminHelpMessage] = useState("");
  const [adminHelpSending, setAdminHelpSending] = useState(false);
  const [adminHelpError, setAdminHelpError] = useState("");
  const [adminHelpSent, setAdminHelpSent] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    {
      role: "assistant",
      text: AI_INTRO_MESSAGE
    }
  ]);
  const [aiInput, setAiInput] = useState("");
  const [aiSending, setAiSending] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiGuideNotice, setAiGuideNotice] = useState("");
  const [aiGuidePlan, setAiGuidePlan] = useState(null);
  const [aiGuideStatus, setAiGuideStatus] = useState(null);
  const [threadReplyDrafts, setThreadReplyDrafts] = useState({});
  const [threadReplySending, setThreadReplySending] = useState({});
  const [threadActionError, setThreadActionError] = useState("");
  const messageRef = useRef(null);
  const aiMessagesStorageKey = useMemo(
    () => (user && familyId ? `aiHelpMessages:${user.uid}:${familyId}` : null),
    [user, familyId]
  );

  useEffect(() => {
    if (!user) return undefined;
    const q = query(collection(db, "feedback"), where("userId", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const next = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      next.sort((a, b) => {
        const aTime = a?.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a?.createdAt || 0).getTime();
        const bTime = b?.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b?.createdAt || 0).getTime();
        return bTime - aTime;
      });
      setThreads(next);
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!aiMessagesStorageKey || typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(aiMessagesStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed) || parsed.length === 0) return;
      const normalized = parsed
        .filter((item) => item && (item.role === "assistant" || item.role === "user"))
        .map((item) => ({
          role: item.role,
          text: String(item.text || "").trim()
        }))
        .filter((item) => item.text);
      if (normalized.length > 0) setAiMessages(normalized);
    } catch {
      // Ignore local storage parse issues and keep default intro message.
    }
  }, [aiMessagesStorageKey]);

  useEffect(() => {
    if (!aiMessagesStorageKey || typeof window === "undefined") return;
    window.localStorage.setItem(aiMessagesStorageKey, JSON.stringify(aiMessages));
  }, [aiMessages, aiMessagesStorageKey]);

  const visibleThreads = useMemo(() => {
    if (!user?.uid) return [];
    return threads.filter((thread) => !isThreadClearedForUser(thread, user.uid));
  }, [threads, user]);

  const feedbackThreads = useMemo(
    () => visibleThreads.filter((thread) => !isHelpThread(thread)),
    [visibleThreads]
  );

  const helpThreads = useMemo(
    () => visibleThreads.filter((thread) => isHelpThread(thread)),
    [visibleThreads]
  );

  const unreadCount = useMemo(() => {
    return visibleThreads.reduce((count, thread) => {
      const responses = Array.isArray(thread.responses) ? thread.responses : [];
      return count + (responses.length ? 1 : 0);
    }, 0);
  }, [visibleThreads]);

  const sendFeedback = async () => {
    if (!user || !familyId) return;
    const trimmed = message.trim();
    if (!trimmed) return;
    setSending(true);
    setFeedbackError("");
    try {
      await submitFeedback({
        familyId,
        message: trimmed,
        platform: "web",
        view: "feedback"
      });
      setMessage("");
    } catch (error) {
      setFeedbackError(
        mapFirebaseError(error, "Unable to send feedback right now. Please try again.")
      );
    } finally {
      setSending(false);
    }
  };

  const sendAdminHelpRequest = async () => {
    if (!user || !familyId) return;
    const trimmed = adminHelpMessage.trim();
    if (!trimmed) return;
    setAdminHelpSending(true);
    setAdminHelpError("");
    try {
      await submitAdminHelpRequest({
        familyId,
        message: trimmed,
        platform: "web",
        view: "help"
      });
      setAdminHelpMessage("");
      setAdminHelpSent(true);
      window.setTimeout(() => setAdminHelpSent(false), 2200);
    } catch (error) {
      setAdminHelpError(
        mapFirebaseError(error, "Unable to request admin help right now. Please try again.")
      );
    } finally {
      setAdminHelpSending(false);
    }
  };

  const mapAiErrorMessage = (error) => {
    const code = String(error?.code || "").toLowerCase();
    const message = String(error?.message || "").trim();
    if (code.includes("unauthenticated")) {
      return "Please sign in again to use AI Help.";
    }
    if (code.includes("permission-denied")) {
      return message || "You do not have permission to use AI Help.";
    }
    return "";
  };

  const applyAiGuide = (guide) => {
    if (!guide || typeof window === "undefined") return;
    const normalizedGuide = {
      ...guide,
      targets: normalizeGuideTargets(guide.targets)
    };
    setAiGuidePlan(buildAiGuidePlan(normalizedGuide));
    const targets = normalizedGuide.targets;
    if (targets.length === 0) return;
    const firstTarget = String(targets[0] || "");
    const firstDetails = getGuideTargetDetails(firstTarget);
    setAiGuideStatus({
      stepId: firstTarget,
      stepIndex: 0,
      total: targets.length,
      stepTitle: firstDetails.title,
      instruction: firstDetails.instruction,
      context: firstDetails.context,
      expectedView: firstDetails.view,
      needsHighlightRequest: true,
      waitingForStep: false,
      autoHighlightPath: false,
      active: false,
      completed: false
    });
    setAiGuideNotice(
      `Step 1 of ${targets.length}: ${firstDetails.title}. Press Highlight Path.`
    );
    window.dispatchEvent(
      new CustomEvent(AI_GUIDE_SESSION_EVENT_START, {
        detail: { guide: normalizedGuide }
      })
    );
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const statusHandler = (event) => {
      const detail = event?.detail || {};
      setAiGuideStatus(detail);
      if (detail.completed) {
        setAiGuideNotice("Guide complete.");
        return;
      }
      const total = Number(detail.total || 0);
      const stepIndex = Number(detail.stepIndex || 0);
      const stepTitle = String(detail.stepTitle || "").trim() || "next step";
      const expectedView = String(detail.expectedView || "").trim();
      const expectedViewLabel = AI_GUIDE_VIEW_LABELS[expectedView] || expectedView;
      const waitingPrefix = expectedViewLabel ? `Open ${expectedViewLabel}, then ` : "";
      const autoPath = detail.autoHighlightPath === true;
      if (detail.needsHighlightRequest) {
        setAiGuideNotice(
          total > 0
            ? `Step ${Math.min(stepIndex + 1, total)} of ${total}: ${waitingPrefix}${stepTitle}. Press Highlight Path.`
            : `${waitingPrefix}${stepTitle}. Press Highlight Path.`
        );
        return;
      }
      if (detail.waitingForStep) {
        setAiGuideNotice(
          total > 0
            ? `Step ${Math.min(stepIndex + 1, total)} of ${total}: ${waitingPrefix}complete ${stepTitle}.${autoPath ? " Path mode will auto-highlight next." : ""}`
            : `${waitingPrefix}complete ${stepTitle}.${autoPath ? " Path mode will auto-highlight next." : ""}`
        );
        return;
      }
      if (detail.active && total > 0) {
        setAiGuideNotice(
          autoPath
            ? `Step ${Math.min(stepIndex + 1, total)} of ${total}: highlighted ${stepTitle}. Complete it and path mode will move to the next step.`
            : `Step ${Math.min(stepIndex + 1, total)} of ${total}: highlighted ${stepTitle}. Complete it and the next step will start automatically.`
        );
      }
    };
    window.addEventListener(AI_GUIDE_SESSION_EVENT_STATUS, statusHandler);
    return () => {
      window.removeEventListener(AI_GUIDE_SESSION_EVENT_STATUS, statusHandler);
    };
  }, []);

  const sendThreadReply = async (thread) => {
    if (!thread?.id) return;
    const text = String(threadReplyDrafts[thread.id] || "").trim();
    if (!text) return;
    setThreadActionError("");
    setThreadReplySending((prev) => ({ ...prev, [thread.id]: true }));
    try {
      await appendSupportThreadMessage({
        feedbackId: thread.id,
        message: text
      });
      setThreadReplyDrafts((prev) => ({ ...prev, [thread.id]: "" }));
    } catch (error) {
      setThreadActionError(
        mapFirebaseError(error, "Unable to send thread reply right now. Please try again.")
      );
    } finally {
      setThreadReplySending((prev) => ({ ...prev, [thread.id]: false }));
    }
  };

  const clearThread = async (thread) => {
    if (!thread?.id) return;
    setThreadActionError("");
    try {
      await clearSupportThreadForUser({
        feedbackId: thread.id
      });
    } catch (error) {
      setThreadActionError(
        mapFirebaseError(error, "Unable to clear this thread right now. Please try again.")
      );
    }
  };

  const renderThreadList = (threadList, emptyMessage) => {
    if (threadList.length === 0) {
      return <div className="feedback-thread-empty">{emptyMessage}</div>;
    }
    return (
      <div className="feedback-thread-list">
        {threadList.map((thread) => (
          <div key={thread.id} className="feedback-thread">
            <div className="feedback-thread-head">
              <div className="feedback-thread-message">{thread.message}</div>
              <button
                className="btn-nav ghost feedback-clear-btn"
                type="button"
                onClick={() => clearThread(thread)}
              >
                Clear
              </button>
            </div>
            {(thread.responses || []).length === 0 ? (
              <div className="feedback-thread-empty">No replies yet.</div>
            ) : (
              <div className="feedback-thread-responses">
                {thread.responses.map((response) => {
                  const fromUser = isResponseFromCurrentUser(response, user);
                  return (
                    <div
                      key={response.id || response.createdAtMs || response.message}
                      className={`feedback-thread-reply ${fromUser ? "user" : "assistant"}`}
                    >
                      <div>{response.message}</div>
                      <div className="feedback-thread-meta">{responseAuthorLabel(response, user)}</div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="feedback-thread-reply-box">
              <textarea
                placeholder="Continue this thread..."
                value={threadReplyDrafts[thread.id] || ""}
                onChange={(event) =>
                  setThreadReplyDrafts((prev) => ({ ...prev, [thread.id]: event.target.value }))
                }
              />
              <div className="feedback-thread-reply-actions">
                <button
                  className="btn-nav"
                  type="button"
                  onClick={() => sendThreadReply(thread)}
                  disabled={threadReplySending[thread.id] || !String(threadReplyDrafts[thread.id] || "").trim()}
                >
                  {threadReplySending[thread.id] ? "Sending..." : "Send Follow-up"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const clearAiChat = () => {
    const intro = {
      role: "assistant",
      text: AI_INTRO_MESSAGE
    };
    setAiMessages([intro]);
    setAiInput("");
    setAiError("");
    setAiGuideNotice("");
    setAiGuidePlan(null);
    setAiGuideStatus(null);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(AI_GUIDE_SESSION_EVENT_CLEAR));
    }
    if (aiMessagesStorageKey && typeof window !== "undefined") {
      window.localStorage.setItem(aiMessagesStorageKey, JSON.stringify([intro]));
    }
  };

  const requestGuidePathHighlight = () => {
    if (!aiGuideStatus?.stepId || typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent(AI_GUIDE_SESSION_EVENT_REQUEST_HIGHLIGHT, {
        detail: { stepId: aiGuideStatus.stepId, mode: "path" }
      })
    );
  };

  const sendAiMessage = async () => {
    if (!user || !familyId) return;
    const trimmed = aiInput.trim();
    if (!trimmed || aiSending) return;

    const userMessage = { role: "user", text: trimmed };
    setAiInput("");
    setAiError("");
    setAiGuideNotice("");
    setAiGuidePlan(null);
    setAiGuideStatus(null);
    setAiSending(true);
    setAiMessages((prev) => [...prev, userMessage]);

    const localGuide = deriveLocalGuideFromMessage(trimmed);
    const localReply = buildLocalAiFallbackReply(trimmed);
    if (localGuide || !isGenericLocalFallbackReply(localReply)) {
      setAiMessages((prev) => [...prev, { role: "assistant", text: localReply }]);
      if (localGuide) applyAiGuide(localGuide);
      setAiSending(false);
      return;
    }

    try {
      const result = await askHelpAssistant({
        familyId,
        message: trimmed
      });
      let reply = String(result?.data?.reply || "").trim();
      const guide = result?.data?.guide || null;
      if (!reply || isWeakAiReply(reply)) {
        reply = buildLocalAiFallbackReply(trimmed);
      }
      setAiMessages((prev) => [...prev, { role: "assistant", text: reply }]);
      if (guide) applyAiGuide(guide);
    } catch (error) {
      setAiError(mapAiErrorMessage(error));
      setAiMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: buildLocalAiFallbackReply(trimmed)
        }
      ]);
    } finally {
      setAiSending(false);
    }
  };

  return (
    <div className="feedback-page" data-tour="feedback-page">
      <div className="glass feedback-page-card">
        <div className="header-flex">
          <div>
            <h2>Support Center</h2>
            <p style={{ opacity: 0.7, margin: "6px 0 0" }}>
              Help Center handles AI + admin help. Feedback Panel handles messages + ongoing threads.
            </p>
          </div>
          {unreadCount > 0 && <span className="pro-badge">Updates</span>}
        </div>

        <div className="feedback-stack">
          <section className="feedback-section">
            <div className="feedback-section-header">
              <h3>Help Center</h3>
            </div>
            <div className="feedback-help-grid">
              <div className="feedback-ai-card">
                <div className="header-flex">
                  <h4>AI Help Panel</h4>
                  <span className="badge">Beta</span>
                </div>
                <div className="feedback-ai-thread">
                  {aiMessages.map((item, index) => (
                    <div
                      key={`${item.role}-${index}`}
                      className={`feedback-ai-message ${item.role === "assistant" ? "assistant" : "user"}`}
                    >
                      <div className="feedback-ai-message-meta">
                        {item.role === "assistant" ? "AI Help" : "You"}
                      </div>
                      <div>{item.text}</div>
                    </div>
                  ))}
                </div>
                <textarea
                  className="feedback-ai-input"
                  placeholder="Ask AI how to use FamilySync..."
                  value={aiInput}
                  onChange={(event) => setAiInput(event.target.value)}
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                      event.preventDefault();
                      sendAiMessage();
                    }
                  }}
                />
                <div className="feedback-ai-actions">
                  <button
                    className="btn-nav success-btn"
                    type="button"
                    onClick={sendAiMessage}
                    disabled={aiSending || !aiInput.trim()}
                  >
                    {aiSending ? "Thinking..." : "Ask AI"}
                  </button>
                  <button className="btn-nav ghost" type="button" onClick={clearAiChat}>
                    Clear Chat
                  </button>
                </div>
                {aiGuideNotice && (
                  <small className="success-message" role="status">
                    {aiGuideNotice}
                  </small>
                )}
                {aiGuideStatus && !aiGuideStatus.completed && (
                  <div className="feedback-ai-guide-current" role="status">
                    <div className="feedback-ai-guide-current-title">
                      Step {Number(aiGuideStatus.stepIndex || 0) + 1} of {Number(aiGuideStatus.total || 0)}:{" "}
                      {aiGuideStatus.stepTitle || "Current step"}
                    </div>
                    {aiGuideStatus.instruction && (
                      <div className="feedback-ai-guide-current-instruction">
                        {aiGuideStatus.instruction}
                      </div>
                    )}
                    {aiGuideStatus.expectedView && (
                      <div className="feedback-ai-guide-current-context">
                        Required screen: {AI_GUIDE_VIEW_LABELS[aiGuideStatus.expectedView] || aiGuideStatus.expectedView}
                      </div>
                    )}
                    {aiGuideStatus.context && (
                      <div className="feedback-ai-guide-current-context">
                        {aiGuideStatus.context}
                      </div>
                    )}
                    <div className="feedback-ai-guide-current-actions">
                      <button
                        className="btn-nav success-btn"
                        type="button"
                        onClick={requestGuidePathHighlight}
                        disabled={Boolean(aiGuideStatus.autoHighlightPath) && aiGuideStatus.active}
                      >
                        {aiGuideStatus.autoHighlightPath ? "Highlight Path Active" : "Highlight Path"}
                      </button>
                    </div>
                  </div>
                )}
                {aiGuidePlan && (
                  <div className="feedback-ai-guide-card" role="status">
                    <div className="feedback-ai-guide-title">{aiGuidePlan.title}</div>
                    <ol className="feedback-ai-guide-steps">
                      {aiGuidePlan.steps.map((step, index) => (
                        <li key={`${step.title}-${index}`}>
                          <div className="feedback-ai-guide-step-title">{step.title}</div>
                          <div className="feedback-ai-guide-step-instruction">{step.instruction}</div>
                          {step.context && (
                            <div className="feedback-ai-guide-step-context">{step.context}</div>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {aiError && (
                  <small className="pin-error" role="alert">
                    {aiError}
                  </small>
                )}
              </div>

              <div className="feedback-help-card">
                <h4>Admin Help Panel</h4>
                <p>Use this when AI does not solve your issue and you need a human admin response.</p>
                <small style={{ opacity: 0.8 }}>
                  Defaults: parent PIN <strong>1234</strong>, younger child PIN <strong>1234</strong> until changed.
                </small>
                <textarea
                  className="feedback-help-input"
                  placeholder="Describe what you need help with for admin..."
                  value={adminHelpMessage}
                  onChange={(event) => setAdminHelpMessage(event.target.value)}
                />
                <button
                  className="btn-nav"
                  type="button"
                  onClick={sendAdminHelpRequest}
                  disabled={adminHelpSending || !adminHelpMessage.trim()}
                >
                  {adminHelpSending ? "Sending..." : "Request Admin Help"}
                </button>
                {adminHelpSent && (
                  <small className="success-message" role="status">
                    Admin help request sent.
                  </small>
                )}
                {adminHelpError && (
                  <small className="pin-error" role="alert">
                    {adminHelpError}
                  </small>
                )}
                <div className="feedback-threads">
                  <div className="feedback-threads-header">
                    <h4>Admin Help Threads</h4>
                  </div>
                  {renderThreadList(
                    helpThreads,
                    "No admin-help threads yet. Send a request above and replies stay here."
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="feedback-section">
            <div className="feedback-section-header">
              <h3>Feedback Panel</h3>
            </div>
            <div className="feedback-panel-grid">
              <div className="feedback-compose">
                <h4>Send Feedback</h4>
                <textarea
                  placeholder="What can we improve?"
                  ref={messageRef}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                />
                <button className="btn-nav success-btn" onClick={sendFeedback} disabled={sending || !message.trim()}>
                  {sending ? "Sending..." : "Send Feedback"}
                </button>
                {feedbackError && (
                  <small className="pin-error" role="alert">
                    {feedbackError}
                  </small>
                )}
              </div>

              <div className="feedback-threads">
                <div className="feedback-threads-header">
                  <h4>Ongoing Threads</h4>
                </div>
                {renderThreadList(
                  feedbackThreads,
                  "No feedback threads yet. Send a note and ongoing replies stay here."
                )}
              </div>
            </div>
            {threadActionError && (
              <small className="pin-error" role="alert">
                {threadActionError}
              </small>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
